import { Button } from '@/components/ui/button';
import { withBasePath } from '@/utils/basePath';
import { getRecaptchaToken, submitContactForm } from '@/services/contact.service';
import allCountries from '@/utils/data/pays.json';
import Image from 'next/image';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import Script from 'next/script';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { isValidEmail, isValidPhone } from '@/utils';

// ─────────────────────────────────────────────────────────────
// 🧪 SIMULATION MODE
// Mettre à `true` pour tester la gestion d'erreurs sans
// spammer l'endpoint réel. En mode simulation :
//   - les erreurs de validation s'affichent normalement
//   - si tout est valide → redirection vers /contact-success
//   - aucun appel réseau n'est effectué
// ─────────────────────────────────────────────────────────────
const IS_SIMULATION = false;

type ContactFormData = {
  cabinet: string;
  pays: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  message: string;
};

type FormErrors = Partial<Record<keyof ContactFormData, string>>;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const INITIAL_FORM: ContactFormData = {
  cabinet: '',
  pays: 'France',
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  message: '',
};

export const getStaticProps: GetStaticProps<{
  recaptchaSiteKey: string | null;
}> = async () => ({
  props: {
    recaptchaSiteKey: process.env.RECAPTCHA_SITE_KEY ?? null,
  },
});

const fieldClassName = (hasError: boolean) =>
  `rounded-md border px-3 py-2 text-sm text-[#394454b5] outline-none transition-colors duration-200 hover:border-orange focus:border-orange ${
    hasError ? 'border-red-400 bg-red-50' : 'border-blue'
  }`;

const labelClassName = 'mb-1 font-medium text-dark-blue';

function validateForm(form: ContactFormData): FormErrors {
  const errors: FormErrors = {};

  if (!form.cabinet.trim()) errors.cabinet = 'Le nom du cabinet est requis.';
  if (!form.nom.trim()) errors.nom = 'Le nom est requis.';
  if (!form.prenom.trim()) errors.prenom = 'Le prénom est requis.';

  if (!form.email.trim()) {
    errors.email = "L'adresse email est requise.";
  } else if (!isValidEmail(form.email)) {
    errors.email = "L'adresse email n'est pas valide.";
  }

  if (form.telephone && !isValidPhone(form.telephone)) {
    errors.telephone = "Le numéro de téléphone n'est pas valide.";
  }

  return errors;
}

function FieldError({ message }: { message?: string }) {
  return (
    <p
      aria-live="polite"
      className={`pointer-events-none absolute top-full left-0 z-10 mt-0.5 flex items-center gap-1 text-xs text-red-500 transition-all duration-200 ease-out ${message ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'} `}
    >
      <svg
        className="h-3 w-3 shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          clipRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          fillRule="evenodd"
        />
      </svg>
      {message ?? ''}
    </p>
  );
}

export default function Contact({
  recaptchaSiteKey,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter();
  const [form, setForm] = useState<ContactFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof ContactFormData, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const countries = useMemo(() => Array.from(new Set(allCountries)), []);

  const handleChange = (event: React.ChangeEvent<{ name: string; value: string }>) => {
    const name = event.target.name as keyof ContactFormData;
    const { value } = event.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const fieldErrors = validateForm({ ...form, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
    }
  };

  const handleBlur = (event: React.FocusEvent<{ name: string }>) => {
    const name = event.target.name as keyof ContactFormData;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const fieldErrors = validateForm(form);
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
  };

  const handleSubmit: React.FormEventHandler = async (event) => {
    event.preventDefault();
    setGlobalError(null);

    const allTouched = Object.fromEntries(
      Object.keys(INITIAL_FORM).map((k) => [k, true]),
    ) as Record<keyof ContactFormData, boolean>;
    setTouched(allTouched);

    const validationErrors = validateForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    if (IS_SIMULATION) {
      setIsSubmitting(true);
      await new Promise((r) => setTimeout(r, 800));
      setIsSubmitting(false);
      await router.push('/contact-success', undefined, { scroll: false });
      return;
    }

    if (!recaptchaSiteKey) {
      setGlobalError('La clé reCAPTCHA est introuvable dans la configuration.');
      return;
    }

    setIsSubmitting(true);

    try {
      const recaptchaToken = await getRecaptchaToken(recaptchaSiteKey);
      await submitContactForm(form, recaptchaToken);

      setForm(INITIAL_FORM);
      await router.push('/contact-success', undefined, { scroll: false });
    } catch (error) {
      setGlobalError(
        error instanceof Error ? error.message : "Une erreur est survenue lors de l'envoi.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {!IS_SIMULATION && recaptchaSiteKey ? (
        <Script
          src={`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`}
          strategy="afterInteractive"
        />
      ) : null}

      <div className="max-width-blue-section flex min-h-[950px] items-start justify-center">
        <div className="mx-auto my-[120px] grid w-full max-w-[1400px] grid-cols-1 overflow-hidden rounded-(--radius-current) bg-transparent sm:bg-white sm:[box-shadow:var(--shadow-current)] xl:grid-cols-2">
          <div className="relative hidden overflow-hidden bg-gray-800 xl:flex xl:items-center xl:justify-center xl:p-16 2xl:p-20">
            <svg
              className="pointer-events-none absolute inset-0"
              height="100%"
              preserveAspectRatio="xMidYMax slice"
              viewBox="0 0 960 540"
              width="100%"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g
                className="text-gray-700 opacity-25"
                fill="none"
                stroke="currentColor"
                strokeWidth="100"
              >
                <circle cx="196" cy="23" r="234" />
                <circle cx="790" cy="491" r="234" />
              </g>
            </svg>
            <svg
              className="absolute -top-16 -right-16 text-gray-700"
              fill="none"
              height="192"
              viewBox="0 0 220 192"
              width="220"
            >
              <defs>
                <pattern
                  height="20"
                  id="contact-pattern"
                  patternUnits="userSpaceOnUse"
                  width="20"
                  x="0"
                  y="0"
                >
                  <rect fill="currentColor" height="4" width="4" x="0" y="0" />
                </pattern>
              </defs>
              <rect fill="url(#contact-pattern)" height="192" width="220" />
            </svg>

            <div className="relative z-10 w-full max-w-2xl">
              <Image
                alt="Logo Zele Solutions"
                className="h-16 w-auto"
                height={64}
                src={withBasePath('/images/logos/zeleLogo.svg')}
                width={180}
                priority
              />
              <h2 className="mt-6 text-left text-4xl! text-white!">
                Un renseignement ?
                <br />
                Une démo ?
                <br />
                Contactez-nous&nbsp;!
              </h2>
              <p className="text-blue! mt-6 text-lg! font-bold">
                zLawyer, la solution de gestion de cabinets d&apos;avocats simple, ergonomique, sans
                engagement&nbsp;!
              </p>
              <div className="mt-14 flex items-center">
                <Image
                  alt="Icône téléphone"
                  height={28}
                  src={withBasePath('/images/logos/phoneLogo.svg')}
                  width={28}
                />
                <p className="ml-4 shrink-0">
                  <a className="text-blue!" href="tel:+33184257027">
                    01 84 25 70 27
                  </a>
                </p>
              </div>
              <div className="mt-14 flex items-center">
                <Image
                  alt="Icône localisation"
                  className="ml-2"
                  height={28}
                  src={withBasePath('/images/logos/locaLogo.svg')}
                  width={28}
                />
                <p className="text-blue! ml-4 shrink-0">
                  Zele Solutions
                  <br />
                  9, rue Angélique Verien,
                  <br />
                  92200, Neuilly-sur-Seine
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 sm:p-10">
            <h2 className="p-2 text-left">Contact</h2>
            <p className="px-2 text-sm text-gray-500">
              Les champs marqués d&apos;un * sont obligatoires.
            </p>

            {globalError && (
              <div
                aria-live="assertive"
                className="mx-2 mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                <svg className="mt-0.5 h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    clipRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    fillRule="evenodd"
                  />
                </svg>
                {globalError}
              </div>
            )}

            {IS_SIMULATION && (
              <div className="mx-2 mt-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700">
                <span className="font-semibold">🧪 Mode simulation activé</span> — aucun email ne
                sera envoyé.
              </div>
            )}

            <form
              className="mt-2 grid grid-cols-1 gap-x-3 gap-y-6 md:grid-cols-2"
              method="POST"
              noValidate
              onSubmit={handleSubmit}
            >
              {/* Cabinet */}
              <div className="relative col-span-2 flex flex-col">
                <label className={labelClassName} htmlFor="cabinet">
                  Cabinet <span className="text-red-600">*</span>
                </label>
                <input
                  className={fieldClassName(!!errors.cabinet)}
                  id="cabinet"
                  name="cabinet"
                  type="text"
                  value={form.cabinet}
                  required
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <FieldError message={errors.cabinet} />
              </div>

              {/* Pays */}
              <div className="col-span-2 flex flex-col">
                <label className={labelClassName} htmlFor="pays">
                  Pays
                </label>
                <select
                  className={fieldClassName(false)}
                  id="pays"
                  name="pays"
                  value={form.pays}
                  onBlur={handleBlur}
                  onChange={handleChange}
                >
                  {countries.map((country, index) => (
                    <option key={index} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              {/* Nom / Prénom */}
              <div className="relative col-span-1 flex flex-col">
                <label className={labelClassName} htmlFor="nom">
                  Nom <span className="text-red-600">*</span>
                </label>
                <input
                  className={fieldClassName(!!errors.nom)}
                  id="nom"
                  name="nom"
                  type="text"
                  value={form.nom}
                  required
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <FieldError message={errors.nom} />
              </div>
              <div className="relative col-span-1 flex flex-col">
                <label className={labelClassName} htmlFor="prenom">
                  Prénom <span className="text-red-600">*</span>
                </label>
                <input
                  className={fieldClassName(!!errors.prenom)}
                  id="prenom"
                  name="prenom"
                  type="text"
                  value={form.prenom}
                  required
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <FieldError message={errors.prenom} />
              </div>

              {/* Email / Téléphone */}
              <div className="relative flex flex-col">
                <label className={labelClassName} htmlFor="email">
                  Mail <span className="text-red-600">*</span>
                </label>
                <input
                  className={fieldClassName(!!errors.email)}
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  required
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <FieldError message={errors.email} />
              </div>
              <div className="relative flex flex-col">
                <label className={labelClassName} htmlFor="telephone">
                  Téléphone
                </label>
                <input
                  className={fieldClassName(!!errors.telephone)}
                  id="telephone"
                  name="telephone"
                  type="tel"
                  value={form.telephone}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <FieldError message={errors.telephone} />
              </div>

              {/* Message */}
              <div className="col-span-2 flex flex-col">
                <label className={labelClassName} htmlFor="message">
                  Message
                </label>
                <textarea
                  className={`${fieldClassName(false)} h-28 resize-none`}
                  id="message"
                  name="message"
                  value={form.message}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </div>

              <Button
                className="col-span-2 mx-2 my-10 w-max disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? 'Envoi en cours…' : 'Envoyer'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
